export type EntryRequestStatus = "pending" | "approved" | "rejected" | "canceled" | "claimed"

export type PendingEntryRequest = {
  status: EntryRequestStatus
  requesterName: string
  // A refusal and a silence are the same status and different news. The server
  // separates them because only it knows which one happened.
  expired: boolean
  // A duration, not an instant: the browser measures it against its own clock,
  // and an absolute timestamp would make a phone running fast stop watching
  // early. See `User::InvitesController#serialize_pending_entry_request`.
  expiresInSeconds: number
}

export type EntryStateInputs = {
  request: PendingEntryRequest | null
}

export type EntryState = {
  waiting: boolean
  refused: boolean
  expired: boolean
  canAsk: boolean
}

/**
 * What the pre-seating menu and the door screen show, derived in one place.
 *
 * The geofence is gone (#155 phase 4): there is no browser-side check left to
 * gate the ask on, so the only inputs left are the request's own status. The
 * ask itself now lives on the menu (`EntryAskPanel`), persistent, rather than
 * behind a confirm-your-location step — extracted from there because these
 * four booleans carry the product rules of the entry-approval flow, and a rule
 * worth arguing about is worth a test that runs on every pull request.
 */
export function entryState({ request }: EntryStateInputs): EntryState {
  const waiting = request?.status === "pending"
  // A person answered no. The ask stays withheld from THIS browser for as
  // long as its claim cookie still names the refused request — at most
  // `Location::EntryRequest::CLAIM_COOKIE_TTL` (20 minutes), counted from
  // when the request was created, not from when it was refused, and cleared
  // early by clearing the cookie or opening a private window, by design.
  // What actually bounds a cleared cookie is the concurrent-pending cap and
  // the token rate limiter server-side, not this latch — finding someone
  // from the restaurant is still the fastest way back in.
  const refused = request?.status === "rejected" && !request.expired
  // Nobody answered inside the window. Asking again overrides no one.
  const expired = request?.status === "rejected" && request.expired

  return {
    waiting,
    refused,
    expired,
    // Nothing asked yet, or the last ask lapsed unanswered: the ask is offered.
    // A live refusal is the one state that withholds it.
    canAsk: !waiting && !refused,
  }
}
